import React, { useState } from 'react';
import { Box, Typography, Chip, Collapse, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  EngineStartupFlight,
  EngineStartupEvent,
  EngineStartupSuccessEvent,
  EngineStartupCheck,
  StartupVerdict,
} from '../../../../types/api';
import { useTranslation } from '../../../../i18n';
import { sharedByLabel } from '../../../../utils/sharedFlights';

const VERDICT_COLORS: Record<StartupVerdict, 'success' | 'warning' | 'error'> = {
  pass: 'success',
  warning: 'warning',
  fail: 'error',
};

const VERDICT_LABELS: Record<StartupVerdict, string> = {
  pass: 'Pass',
  warning: 'Warning',
  fail: 'Fail',
};

function isSuccessEvent(event: EngineStartupEvent): event is EngineStartupSuccessEvent {
  return 'overall_verdict' in event && 'checks' in event;
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

interface CheckResultRowProps {
  check: EngineStartupCheck;
}

const CheckResultRow: React.FC<CheckResultRowProps> = ({ check }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 0.75,
      px: 1,
      '&:not(:last-child)': { borderBottom: '1px solid #f0f0f0' },
    }}
  >
    <Chip
      label={VERDICT_LABELS[check.verdict]}
      color={VERDICT_COLORS[check.verdict]}
      size="small"
      sx={{ minWidth: 70 }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {check.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {check.value !== null ? `Value: ${check.value}` : 'N/A'}
        {check.threshold ? ` | Threshold: ${check.threshold}` : ''}
      </Typography>
      {check.details && (
        <Typography variant="caption" color="text.secondary" display="block">
          {check.details}
        </Typography>
      )}
    </Box>
  </Box>
);

interface StartupEventCardProps {
  event: EngineStartupEvent;
  eventIndex: number;
  totalEvents: number;
}

const StartupEventCard: React.FC<StartupEventCardProps> = ({ event, eventIndex, totalEvents }) => {
  const [expanded, setExpanded] = useState(false);

  if (!isSuccessEvent(event)) {
    return (
      <Box sx={{ p: 1, bgcolor: '#fff3e0', borderRadius: 1, mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {totalEvents > 1 ? `Event ${eventIndex + 1}: ` : ''}
          Error - {event.error}
        </Typography>
      </Box>
    );
  }

  const verdict = event.overall_verdict;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {totalEvents > 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 55 }}>
            Event {eventIndex + 1}
          </Typography>
        )}
        <Chip label={VERDICT_LABELS[verdict]} color={VERDICT_COLORS[verdict]} size="small" />
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1, pb: 1 }}>
          {event.checks.map((check) => (
            <CheckResultRow key={check.check_id} check={check} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

interface FlightCardProps {
  flight: EngineStartupFlight;
}

const FlightCard: React.FC<FlightCardProps> = ({ flight }) => {
  const hasEvents = flight.events !== null && flight.events.length > 0;
  const { t } = useTranslation();
  // A flight another organization shared names its owner (#1693)
  const sharedBy = sharedByLabel(t, flight);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        bgcolor: '#fafafa',
      }}
    >
      {/* Flight header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: hasEvents ? 1 : 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>
          {formatDate(flight.flight_date)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          noWrap
          title={flight.filename}
        >
          {flight.filename || 'Unknown file'}
        </Typography>
        {sharedBy && <Chip label={sharedBy} size="small" variant="outlined" />}
      </Box>

      {/* Events or "no startup" */}
      {!hasEvents && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No startup detected
        </Typography>
      )}
      {hasEvents &&
        flight.events!.map((event, idx) => (
          <StartupEventCard
            key={idx}
            event={event}
            eventIndex={idx}
            totalEvents={flight.events!.length}
          />
        ))}
    </Box>
  );
};

interface EngineStartupTimelineProps {
  flights: EngineStartupFlight[];
}

export const EngineStartupTimeline: React.FC<EngineStartupTimelineProps> = ({ flights }) => {
  if (flights.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No engine startup analysis data available for this drone.
      </Typography>
    );
  }

  return (
    <Box>
      {flights.map((flight) => (
        <FlightCard key={flight.flight_id} flight={flight} />
      ))}
    </Box>
  );
};
